<template>
	<div
		class="site-condensed-bar"
		:class="{ 'site-condensed-bar--shown': shown, 'site-condensed-bar--static': staticMode }"
		:style="barStyle"
		role="banner"
		aria-label="Site navigation"
	>
		<!-- designed floating header: the host renders its sections through the `blocks` slot -->
		<div v-if="designed" class="site-condensed-bar__designed">
			<slot name="blocks" :blocks="floatingBlocks"></slot>
		</div>
		<!-- fallback until one is designed: logo + the header's menu, compact -->
		<div v-else class="site-condensed-bar__inner" :style="{ maxWidth: pageWidth }">
			<a class="site-condensed-bar__logo" :href="staticMode ? null : '/'" :aria-label="siteName" @click="onLogoClick">
				<img v-if="logoSrc" v-bind="logoAttrs" :alt="siteName" class="site-condensed-bar__img">
				<span v-else class="site-condensed-bar__name">{{ siteName }}</span>
			</a>
			<div v-if="navBlock" class="site-condensed-bar__nav">
				<slot name="nav" :block="navBlock"></slot>
			</div>
		</div>
	</div>
</template>

<script>
/**
 * condensedHeaderBar.vue
 * ──────────────────────
 * The thin bar that floats at the top once the full header has scrolled
 * away (Site Styles → Layout → Sticky header → Thin bar). It shows the site
 * logo and the header's navigation menu in a compact form.
 *
 * Shared by the public renderer and the editor canvas. When the site has a
 * designed Floating header region, the host renders those sections through
 * the `blocks` slot; otherwise the fallback (logo + the header's menu) is
 * rendered through the `nav` slot. Either way the host supplies its own block
 * renderer, so this component stays free of either bundle's registry. The host passes the
 * NavigationBlock found in the header; `condensedNav()` clones it with the
 * `condensed` flag the block uses to tighten its spacing.
 *
 * Public mode: fixed to the viewport; `shown` toggles with scroll position.
 * Static mode (editor): rendered in place as a preview, always visible.
 */
import { imgAttrs } from '@/websites/cms/blocks/shared/imageSrc.js';

/** First NavigationBlock inside a list of header blocks (looks inside sections and columns). */
export function findNavigationBlock(blocks) {
	var found = null;
	var walk = function (list) {
		(list || []).forEach(function (b) {
			if (found || !b) return;
			if (b.type === 'NavigationBlock') { found = b; return; }
			var cols = b.props && b.props.columns;
			if (Array.isArray(cols)) cols.forEach(function (c) { walk(c && c.blocks); });
			if (b.props && Array.isArray(b.props.blocks)) walk(b.props.blocks);
		});
	};
	walk(blocks);
	return found;
}

/** A copy of the header's navigation block flagged for the thin bar. */
export function condensedNav(block) {
	if (!block) return null;
	var copy = JSON.parse(JSON.stringify(block));
	copy.id = (block.id || 'nav') + '__condensed';
	copy.props = Object.assign({}, copy.props, { condensed: true, stickyNav: false });
	return copy;
}

export default {
	name: 'CondensedHeaderBar',

	props: {
		headerBlocks: { type: Array, default: function () { return []; } },
		/** The designed Floating header region; when it has blocks they replace the fallback bar. */
		floatingBlocks: { type: Array, default: function () { return []; } },
		siteSettings: { type: Object, default: function () { return {}; } },
		/** Editor preview: in flow, always shown, no links. */
		staticMode: { type: Boolean, default: false },
		/** Element whose bottom edge decides when the bar appears (public mode). */
		watchEl: { default: null }
	},

	data: function () {
		return { shown: this.staticMode, onScroll: null };
	},

	computed: {
		designed: function () { return Array.isArray(this.floatingBlocks) && this.floatingBlocks.length > 0; },
		navBlock: function () { return condensedNav(findNavigationBlock(this.headerBlocks)); },
		logoSrc: function () { return (this.siteSettings && this.siteSettings.logoSrc) || ''; },
		siteName: function () { return (this.siteSettings && this.siteSettings.siteName) || 'Home'; },
		pageWidth: function () { return (this.siteSettings && this.siteSettings.pageWidth) || '1000px'; },
		logoAttrs: function () { return imgAttrs(this.logoSrc, { widths: [320], fallbackWidth: 320, sizes: '160px' }); },
		barStyle: function () {
			var s = this.siteSettings || {};
			return {
				'--bar-bg': s.condensedBarBg || s.pageColor || '#FFFFFF',
				'--bar-color': s.condensedBarColor || s.bodyColor || '#1E2031'
			};
		}
	},

	mounted: function () {
		if (this.staticMode || typeof window === 'undefined') return;
		var self = this;
		this.onScroll = function () {
			var el = self.watchEl && self.watchEl.getBoundingClientRect ? self.watchEl : null;
			var edge = el ? el.getBoundingClientRect().bottom : 240;
			self.shown = edge < 0;
		};
		window.addEventListener('scroll', this.onScroll, { passive: true });
		window.addEventListener('resize', this.onScroll);
		this.onScroll();
	},

	beforeDestroy: function () {
		if (this.onScroll && typeof window !== 'undefined') {
			window.removeEventListener('scroll', this.onScroll);
			window.removeEventListener('resize', this.onScroll);
		}
	},

	methods: {
		onLogoClick: function (e) {
			if (this.staticMode) e.preventDefault();
		}
	}
};
</script>

<style scoped>
.site-condensed-bar {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	background: var(--bar-bg);
	color: var(--bar-color);
	box-shadow: 0 2px 10px rgba(0, 0, 0, .12);
	transform: translateY(-100%);
	opacity: 0;
	transition: transform .22s ease, opacity .22s ease;
	pointer-events: none;
}
.site-condensed-bar--shown { transform: translateY(0); opacity: 1; pointer-events: auto; }
.site-condensed-bar--static { position: sticky; transform: none; opacity: 1; pointer-events: auto; }
.site-condensed-bar__inner {
	display: flex;
	align-items: center;
	gap: 18px;
	margin: 0 auto;
	padding: 0 16px;
	min-height: 48px;
}
.site-condensed-bar__logo { display: inline-flex; align-items: center; flex: 0 0 auto; color: inherit; text-decoration: none; }
.site-condensed-bar__img { display: block; height: 28px; width: auto; max-width: 180px; object-fit: contain; }
.site-condensed-bar__name { font-family: var(--site-heading-font, inherit); font-weight: 700; font-size: 1.05em; white-space: nowrap; }
.site-condensed-bar__nav { flex: 1 1 auto; min-width: 0; }
.site-condensed-bar__designed { width: 100%; }
@media (prefers-reduced-motion: reduce) { .site-condensed-bar { transition: none; } }
</style>
